import { Controller } from "@hotwired/stimulus"

// Minimal autocomplete for the WFTDA knowledge-base team search. Results are
// plain anchor links, so selecting a team simply navigates to its analysis page.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    delay: { type: Number, default: 300 },
    queryParam: { type: String, default: "q" },
  }

  connect() {
    this.hide()
    this.inputTarget.setAttribute("autocomplete", "off")
    this.inputTarget.setAttribute("spellcheck", "false")

    this.mouseDown = false
    this.onInput = debounce(this.onInput.bind(this), this.delayValue)

    this.inputTarget.addEventListener("input", this.onInput)
    this.inputTarget.addEventListener("blur", this.onBlur)
    this.resultsTarget.addEventListener("mousedown", this.onResultsMouseDown)
    window.addEventListener("scroll", this.updatePosition, true)
    window.addEventListener("resize", this.updatePosition)

    if (this.inputTarget.hasAttribute("autofocus")) this.inputTarget.focus()
  }

  disconnect() {
    this.inputTarget.removeEventListener("input", this.onInput)
    this.inputTarget.removeEventListener("blur", this.onBlur)
    this.resultsTarget.removeEventListener("mousedown", this.onResultsMouseDown)
    window.removeEventListener("scroll", this.updatePosition, true)
    window.removeEventListener("resize", this.updatePosition)
  }

  // Render results as a fixed overlay so the modal's overflow-hidden body does
  // not clip the dropdown.
  updatePosition = () => {
    if (this.resultsTarget.hidden) return
    const rect = this.inputTarget.getBoundingClientRect()
    Object.assign(this.resultsTarget.style, {
      position: "fixed",
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: "9999",
    })
  }

  onInput = () => {
    const query = this.inputTarget.value.trim()
    if (query.length >= this.minLengthValue) {
      this.fetchResults(query)
    } else {
      this.clear()
    }
  }

  onBlur = () => {
    if (this.mouseDown) return
    this.hide()
  }

  onResultsMouseDown = () => {
    this.mouseDown = true
    this.resultsTarget.addEventListener("mouseup", () => { this.mouseDown = false }, { once: true })
  }

  fetchResults = async (query) => {
    if (!this.hasUrlValue) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.append(this.queryParamValue, query)

    const response = await fetch(url.toString(), { headers: { "X-Requested-With": "XMLHttpRequest" } })
    if (!response.ok) return

    this.resultsTarget.innerHTML = await response.text()
    this.show()
  }

  clear() {
    this.resultsTarget.innerHTML = ""
    this.hide()
  }

  show() {
    this.resultsTarget.hidden = false
    this.updatePosition()
  }
  hide() { this.resultsTarget.hidden = true }
}

const debounce = (fn, delay = 300) => {
  let timeoutId = null
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}
